import React, {useCallback, useEffect, useId, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {appPathAfterLocale, withLocalePath} from 'i18n/localePaths'
import {fetchClientCountryCode} from 'i18n/fetchClientCountryCode'
import {appLocaleForCountryCode} from 'i18n/localeFromCountry'
import {
  dismissIpLocalePair,
  isIpLocalePairDismissed,
} from 'i18n/localeIpSuggestStorage'
import {
  AppLocale,
  LOCALE_FLAGS,
  LOCALE_LABELS,
  appLocaleToBcp47,
  isAppLocale,
  writeCachedLocale,
} from 'i18n/locales'
import {useI18n} from 'i18n/I18nContext'

const A11Y_EN = 'Location is approximated, not stored.'

const overlayZ = 10060

export function IpLocaleSuggestDialog() {
  const {t, tInLocale, locale: current} = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [suggested, setSuggested] = useState<AppLocale | null>(null)
  const a11yId = useId()
  const localeRef = useRef<AppLocale | null>(null)

  useEffect(() => {
    localeRef.current = current
  }, [current])

  const handleStay = useCallback(() => {
    if (!suggested) return
    dismissIpLocalePair(current, suggested)
    setOpen(false)
  }, [current, suggested])

  useEffect(() => {
    setOpen(false)
    setSuggested(null)
  }, [current])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleStay()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open, handleStay])

  useEffect(() => {
    const started = current
    const params = new URLSearchParams(location.search)
    const raw = params.get('localeSuggest') ?? params.get('tryLocale')
    const isDev = process.env.NODE_ENV === 'development'
    if (isDev && raw && isAppLocale(raw) && raw !== started) {
      params.delete('localeSuggest')
      params.delete('tryLocale')
      const q = params.toString()
      navigate(
        {pathname: location.pathname, search: q ? `?${q}` : ''},
        {replace: true},
      )
      setSuggested(raw)
      setOpen(true)
      return
    }

    const ab = new AbortController()
    const tmo = window.setTimeout(() => ab.abort(), 8000)

    ;(async () => {
      const cc = await fetchClientCountryCode(ab.signal)
      if (ab.signal.aborted || started !== localeRef.current) return
      const match = appLocaleForCountryCode(cc)
      if (!match || match === started) return
      if (isIpLocalePairDismissed(started, match)) return
      setSuggested(match)
      setOpen(true)
    })().catch(() => {})

    return () => {
      clearTimeout(tmo)
      ab.abort()
    }
  }, [current, location.search, location.pathname, navigate])

  if (!open || !suggested || suggested === current) return null

  const toPath = withLocalePath(
    suggested,
    appPathAfterLocale(location.pathname),
  )
  const search = location.search || ''
  const href = toPath + search
  const acceptCta = `${tInLocale(suggested, 'localeSuggest.switch')}\u00a0${LOCALE_LABELS[suggested]}`

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Language"
      aria-describedby={a11yId}
      className="ip-locale-backdrop"
      style={{zIndex: overlayZ}}
      onClick={handleStay}
    >
      <div className="ip-locale-sr" id={a11yId}>
        {A11Y_EN} Current: {LOCALE_LABELS[current]}. Suggested:{' '}
        {LOCALE_LABELS[suggested]}.
      </div>
      <div className="ip-locale-card" onClick={(e) => e.stopPropagation()}>
        <div className="ip-locale-card__flags" aria-hidden>
          <div
            className="ip-locale-card__flag-wrap ip-locale-card__flag-wrap--dim"
            title={LOCALE_LABELS[current]}
          >
            {LOCALE_FLAGS[current]}
          </div>
          <div className="ip-locale-card__arrow">
            <ArrowForwardIcon sx={{fontSize: 28}} />
          </div>
          <div
            className="ip-locale-card__flag-wrap ip-locale-card__flag-wrap--suggested"
            title={LOCALE_LABELS[suggested]}
          >
            {LOCALE_FLAGS[suggested]}
          </div>
        </div>

        <div
          className="ip-locale-card__actions"
          role="group"
          aria-label="Language"
        >
          <button
            type="button"
            className="ip-locale-card__btn"
            onClick={handleStay}
            aria-label={`${t('localeSuggest.stay')}, ${LOCALE_LABELS[current]}.`}
          >
            <span className="ip-locale-card__btn-ico" aria-hidden>
              <CloseIcon sx={{fontSize: 20, color: 'currentColor'}} />
            </span>
            {t('localeSuggest.stay')}
          </button>
          <button
            type="button"
            className="ip-locale-card__btn ip-locale-card__btn--primary"
            lang={appLocaleToBcp47(suggested)}
            onClick={() => {
              writeCachedLocale(suggested)
              setOpen(false)
              navigate(href, {replace: true})
            }}
            aria-label={acceptCta}
          >
            <span className="ip-locale-card__btn-ico" aria-hidden>
              <CheckIcon sx={{fontSize: 20, color: 'currentColor'}} />
            </span>
            {acceptCta}
          </button>
        </div>
      </div>
    </div>
  )
}
